import { useEffect } from "react";
import { useSelector } from "react-redux";

import { useAppendConsent } from "@/api2/Consent/mutations/useAppendConsent";
import { usePolicyMetadataQuery } from "@/api2/Consent/queries/usePolicyMetadata";
import { AcpStatusBadge } from "@/components/ACP/AcpStatusBadge";
import { SUPPORT_EMAIL_HREF, SUPPORT_NUMBER_HREF } from "@/constants/aptihealth";
import { ConsentedPolicyEnum } from "@/core/constants";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { getConsentPayload } from "@/lib/helpers/getConsentPayload";
import { getCurrentPolicyVersion } from "@/lib/helpers/getCurrentPolicyVersion";
import { useACPStatus } from "@/lib/hooks/useACPStatus";
import { showToast } from "@/providers/ToastProvider";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { selectIsGlobalLoading } from "@/redux/selectors/loaderSelectors";
import { Button2 } from "@/ui2/atoms/Button";
import { Link2 } from "@/ui2/atoms/Link";
import { DashboardCard2 } from "@/ui2/molecules/DashboardCard2";
import { getAutomationDataAttr } from "@/utils/automationUtils";

export function Consents() {
  const profile = useSelector(selectProfile);
  const isLoadingGlobal = useSelector(selectIsGlobalLoading);
  const { isLoading, isSuccess, mutateAsync: appendConsent } = useAppendConsent();
  const { data: policyMetadata } = usePolicyMetadataQuery();
  const consentStatus = useACPStatus();
  const isOptedIn = consentStatus === "OPTED_IN";
  const isOptedOut = consentStatus === "OPTED_OUT";
  const isNotResponded = consentStatus === "NOT_RESPONDED";

  const patientId = profile?.username;
  const policyVersion = getCurrentPolicyVersion({
    policy: "ADVANCED_CARE_PROGRAM",
    policyMetadata,
  });

  const handleConsentSubmit = async () => {
    if (!policyMetadata || !patientId) return;

    try {
      const payload = getConsentPayload({
        consentResponse: true,
        consentType: ConsentedPolicyEnum["ADVANCED_CARE_PROGRAM"],
        patientId,
        policyMetadata,
        policyVersion,
      });

      await appendConsent(payload);
    } catch (error) {
      captureErrorMessage(parseError({ error, fallback: "Consent submission error" }), {
        tags: {
          component: "Consents",
          consentType: "ADVANCED_CARE_PROGRAM",
          errorType: "consent_submission",
          patientId: patientId || "unknown",
        },
      });

      showToast([
        {
          heading: "Something went wrong. Please reload the page and try again.",
          variant: "error",
        },
      ]);
    }
  };

  // trigger toast after both mutation and loader conclude
  useEffect(() => {
    if (!isLoading && !isLoadingGlobal && isSuccess) {
      showToast([{ heading: "Advanced Care Program consent confirmed.", variant: "success" }]);
    }
  }, [isLoading, isLoadingGlobal, isSuccess]);

  return (
    <DashboardCard2
      Actions={(isOptedIn || isOptedOut) && <AcpStatusBadge status={consentStatus} />}
      classNameChildren="tw:flex tw:flex-col tw:gap-4 tw:items-start"
      title="Advanced Care Program Consent"
    >
      <p className="body-md tw:md:max-w-3/5" {...getAutomationDataAttr("consents-acp-description")}>
        Your insurance may cover additional services to support your care journey. By providing
        consent, you agree to participate in the{" "}
        {/* Link to www because using Policy component and /advanced-care route broke routing */}
        <Link2 href="https://www.aptihealth.com/advanced-care-program" isExternal>
          Advanced Care Program
        </Link2>
        .<br />
        Your care team will determine if these services are right for you.
      </p>

      {(isOptedOut || isNotResponded) && (
        <Button2
          className="tw:mt-2"
          isLoading={isLoading || isSuccess}
          onPress={handleConsentSubmit}
          size="lg"
          {...getAutomationDataAttr("consents-agree-to-acp-button")}
        >
          Agree to Advanced Care Program
        </Button2>
      )}

      {isOptedIn && (
        <>
          <hr className="tw:!m-0 tw:w-full" />

          <p className="body-sm" {...getAutomationDataAttr("consents-acp-support-contact")}>
            If you wish to opt-out of the program, please contact us at
            <Link2 className="tw:mx-0.5ch" href={SUPPORT_EMAIL_HREF}>
              support@aptihealth.com
            </Link2>
            or call us at <Link2 href={SUPPORT_NUMBER_HREF}>(888) 454-3827</Link2>.
          </p>
        </>
      )}
    </DashboardCard2>
  );
}
